<script setup lang="ts">
import { computed } from 'vue';

/**
* Load all SVG files under /src/assets/icons as raw strings at build time.
* Keys will be absolute (e.g., '/src/assets/icons/search.svg').
*/
const rawIcons = import.meta.glob('../../assets/flowbite/*.svg', {
  query: '?raw',
  import: 'default',
  eager: true
}) as Record<string, string>;

/** Normalize to { [name]: svgString } -- where name is filename without extension */
const icons: Record<string, string> = {};
for (const [path, svg] of Object.entries(rawIcons)) {
  const file = path.split('/').pop()!;
  const name = file.replace(/\.svg$/i, '');
  icons[name] = svg;
}
type IconName = keyof typeof icons;

interface Props {
  /** Filename without .svg (e.g., "search" -> src/assets/icons/search.svg) */
  name: IconName;
  /** Tailwind sizing classes applied to wrapper (e.g., 'h-4 w-4'). Default: 'h-5 w-5' */
  sizeClass?: string;
  /**
   * Color token or direct Tailwind class.
   * If you pass 'primary', we apply 'text-primary'.
   * If you pass 'text-blue-500', we use it directly.
   */
  color?: string;
  /** 'fill' for solid icons, 'stroke' for outline icon packs */
  variant?: 'fill' | 'stroke';
  /** If true, hide from screen readers */
  decorative?: boolean;
  /** Accessible label when decorative=false */
  ariaLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  sizeClass: 'h-6 w-6',
  color: 'primary',
  variant: 'fill',
  decorative: true,
  ariaLabel: ''
});

// Resolve SVG content by name
const svg = computed(() => icons[props.name]);

/**
* Map token -> Tailwind class (you can extend this map as you like).
* If `color` already looks like a tailwind class (starts with `text-`), we use it directly.
*/
function resolveColorClass(color: string): string {
  if (color.startsWith('text-')) return color;
  return `text-${color}`; // e.g., 'primary' -> 'text-primary'
}

const wrapperClass = computed(() => [
  'inline-block align-middle',
  props.sizeClass,
  resolveColorClass(props.color),
  props.variant === 'stroke' ? 'icon--stroke' : 'icon--fill'
]);

// a11y
const role = computed(() => (props.decorative ? undefined : 'img'));
const ariaHidden = computed(() => (props.decorative ? 'true' : undefined));
const ariaLabel = computed(() => (props.decorative ? undefined : props.ariaLabel ?? String(props.name)));
</script>

<template>
  <!-- eslint-disable vue/no-v-html -- SVG is statically imported at build time, not user-supplied -->
  <span
    v-if="svg"
    :class="wrapperClass"
    :role="role"
    :aria-hidden="ariaHidden"
    :aria-label="ariaLabel"
    v-html="svg"
  />
  <!-- eslint-enable vue/no-v-html -->
  <span
    v-else
    class="inline-block align-middle text-danger-600"
  >
    Icon "{{ name }}" not found
  </span>
</template>

<style scoped>
/* Let the SVG fill the wrapper's Tailwind-defined size */
:deep(svg) {
  width: 100%;
  height: 100%;
  display: block;
}

/* Default: filled icons honor text color via currentColor */
.icon--fill :deep(svg) {
  fill: currentColor;
}

/* For outline icon packs */
.icon--stroke :deep(svg) {
  fill: none;
  stroke: currentColor;
  stroke-width: 1.5;
  stroke-linecap: round;
  stroke-linejoin: round;
}
</style>
